import React, { useEffect, useState } from "react";
import $ from "jquery";
import { Edit3, Image as ImageIcon, Save, Upload } from "lucide-react";
import ApiURl from "../../controllers/Api";

const EMPTY = {
  id: "",
  title: "",
  location: "",
  date_text: "",
  description: "",
  image_path: "",
  sort_order: 1,
  is_active: "1",
};

const EMPTY_GALLERY = {
  id: "",
  title: "",
  image_path: "",
  sort_order: 1,
  is_active: "1",
};

export default function AdminTradeFairs() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryForm, setGalleryForm] = useState(EMPTY_GALLERY);
  const [galleryImageFile, setGalleryImageFile] = useState(null);
  const [galleryPreview, setGalleryPreview] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingGallery, setSavingGallery] = useState(false);

  const fetchItems = async () => {
    try {
      const response = await $.getJSON(`${ApiURl}getTradeFairs.php`);
      setItems(response?.success ? response.data || [] : []);
    } catch (error) {
      console.error("Error loading trade fairs:", error);
      setMessage("Could not load trade fairs.");
    }
  };

  const fetchGallery = async () => {
    try {
      const response = await $.getJSON(`${ApiURl}getTradeFairGallery.php`);
      setGalleryItems(response?.success ? response.data || [] : []);
    } catch (error) {
      console.error("Error loading trade fair gallery:", error);
      setMessage("Could not load trade fair gallery.");
    }
  };

  useEffect(() => {
    fetchItems();
    fetchGallery();
  }, []);

  const saveItem = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");

    const payload = new FormData();
    Object.entries(form).forEach(([key, value]) => payload.append(key, value));
    if (imageFile) payload.append("image", imageFile);

    try {
      const response = await $.ajax({
        url: `${ApiURl}saveTradeFair.php`,
        type: "POST",
        data: payload,
        processData: false,
        contentType: false,
        dataType: "json",
      });
      setMessage(response?.message || "Trade fair saved.");
      if (response?.success) {
        setForm(EMPTY);
        setImageFile(null);
        setPreview("");
        fetchItems();
      }
    } catch (error) {
      console.error("Error saving trade fair:", error);
      setMessage("Server error while saving trade fair.");
    } finally {
      setSaving(false);
    }
  };

  const editItem = (item) => {
    setForm({
      id: item.id || "",
      title: item.title || "",
      location: item.location || "",
      date_text: item.date_text || "",
      description: item.description || "",
      image_path: item.image_path || "",
      sort_order: item.sort_order || 1,
      is_active: String(item.is_active ?? 1),
    });
    setImageFile(null);
    setPreview(item.image_url || "");
    setMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveGalleryItem = async (event) => {
    event.preventDefault();
    setSavingGallery(true);
    setMessage("");

    const payload = new FormData();
    Object.entries(galleryForm).forEach(([key, value]) => payload.append(key, value));
    if (galleryImageFile) payload.append("image", galleryImageFile);

    try {
      const response = await $.ajax({
        url: `${ApiURl}saveTradeFairGallery.php`,
        type: "POST",
        data: payload,
        processData: false,
        contentType: false,
        dataType: "json",
      });
      setMessage(response?.message || "Gallery photo saved.");
      if (response?.success) {
        setGalleryForm(EMPTY_GALLERY);
        setGalleryImageFile(null);
        setGalleryPreview("");
        fetchGallery();
      }
    } catch (error) {
      console.error("Error saving trade fair gallery:", error);
      setMessage(error?.responseJSON?.message || "Server error while saving gallery photo.");
    } finally {
      setSavingGallery(false);
    }
  };

  const editGalleryItem = (item) => {
    setGalleryForm({
      id: item.id || "",
      title: item.title || "",
      image_path: item.image_path || "",
      sort_order: item.sort_order || 1,
      is_active: String(item.is_active ?? 1),
    });
    setGalleryImageFile(null);
    setGalleryPreview(item.image_url || "");
    setMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="min-h-screen bg-[#F7EFE7] p-4 font-[Kanit] text-[#2B0000] lg:p-7">
      <div className="mx-auto max-w-[1200px]">
        <section className="mb-6 rounded-[24px] border border-[#d8c6b7] bg-[#fffaf5] p-5 shadow-sm">
          <h1 className="font-serif text-4xl">Trade Fairs</h1>
          <p className="mt-2 text-sm text-[#6f5e57]">Add, edit and hide/show trade fair entries on the public page.</p>
        </section>

        {message && <div className="mb-5 rounded-xl border border-[#d4af37]/30 bg-[#fff7e6] px-4 py-3 text-sm font-semibold text-[#7d5a19]">{message}</div>}

        <form onSubmit={saveItem} className="mb-7 rounded-[24px] border border-[#d8c6b7] bg-white p-5 shadow-sm">
          <h2 className="mb-4 font-serif text-2xl">{form.id ? "Update Trade Fair" : "Add Trade Fair"}</h2>
          <div className="grid gap-4 lg:grid-cols-2">
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" className="rounded-xl border border-[#d6c4b5] px-4 py-3 outline-none" />
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location" className="rounded-xl border border-[#d6c4b5] px-4 py-3 outline-none" />
            <input value={form.date_text} onChange={(e) => setForm({ ...form, date_text: e.target.value })} placeholder="Date text" className="rounded-xl border border-[#d6c4b5] px-4 py-3 outline-none" />
            <input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} placeholder="Sort order" className="rounded-xl border border-[#d6c4b5] px-4 py-3 outline-none" />
          </div>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" rows={5} className="mt-4 w-full rounded-xl border border-[#d6c4b5] px-4 py-3 outline-none" />
          <select value={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.value })} className="mt-4 rounded-xl border border-[#d6c4b5] px-4 py-3 outline-none">
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>

          <div className="mt-4 rounded-2xl border border-[#decfc3] bg-[#fffaf5] p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
              <ImageIcon className="h-4 w-4 text-[#8f6f38]" />
              Trade Fair Photo
            </div>
            <div className="mb-4 h-56 overflow-hidden rounded-xl border border-[#d8c6b7] bg-[#fbf3eb]">
              {preview ? <img src={preview} alt="Trade fair preview" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-sm text-[#9b877e]">No image selected</div>}
            </div>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm font-semibold">
              <Upload className="h-4 w-4" />
              Upload Photo
              <input type="file" accept="image/*" onChange={(event) => {
                const file = event.target.files?.[0] || null;
                setImageFile(file);
                if (file) setPreview(URL.createObjectURL(file));
              }} className="hidden" />
            </label>
          </div>

          <div className="mt-5 flex gap-3">
            <button disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-[#2B0000] px-5 py-3 text-sm font-semibold text-[#f4dfae] disabled:opacity-60">
              <Save className="h-4 w-4" />
              {saving ? "Saving..." : form.id ? "Update Trade Fair" : "Add Trade Fair"}
            </button>
            {form.id && <button type="button" onClick={() => { setForm(EMPTY); setImageFile(null); setPreview(""); }} className="rounded-xl border border-[#d6c4b5] px-5 py-3 text-sm font-semibold">Cancel</button>}
          </div>
        </form>

        <section className="overflow-hidden rounded-[24px] border border-[#d8c6b7] bg-[#fffaf5] shadow-sm">
          {items.map((item) => (
            <div key={item.id} className="flex flex-wrap items-center justify-between gap-4 border-b border-[#eadfd5] p-4 last:border-b-0">
              <div className="flex min-w-0 items-center gap-4">
                <span className="grid h-20 w-28 shrink-0 overflow-hidden rounded-xl bg-[#fbf3eb] text-xs text-[#9b877e]">
                  {item.image_url ? <img src={item.image_url} alt={item.title} className="h-full w-full object-cover" /> : "No image"}
                </span>
                <div className="min-w-0">
                  <h3 className="truncate font-serif text-xl">{item.title}</h3>
                  <p className="text-sm text-[#7d6c64]">Order {item.sort_order} - {item.is_active ? "Active" : "Inactive"}</p>
                </div>
              </div>
              <button onClick={() => editItem(item)} className="inline-flex items-center gap-2 rounded-xl border border-[#d6c4b5] bg-white px-4 py-2.5 text-sm font-semibold">
                <Edit3 className="h-4 w-4" />
                Edit
              </button>
            </div>
          ))}
          {!items.length && <p className="p-6 text-sm text-[#7d6c64]">No trade fairs added yet.</p>}
        </section>

        <section className="mt-8 rounded-[24px] border border-[#d8c6b7] bg-[#fffaf5] p-5 shadow-sm">
          <h2 className="font-serif text-3xl">Trade Fair Gallery</h2>
          <p className="mt-2 text-sm text-[#6f5e57]">Upload gallery photos separately from the trade fair timeline cards.</p>

          <form onSubmit={saveGalleryItem} className="mt-5 rounded-[20px] border border-[#eadfd5] bg-white p-4">
            <div className="grid gap-4 lg:grid-cols-[1fr_150px_160px]">
              <input
                value={galleryForm.title}
                onChange={(e) => setGalleryForm({ ...galleryForm, title: e.target.value })}
                placeholder="Optional gallery title"
                className="rounded-xl border border-[#d6c4b5] px-4 py-3 outline-none"
              />
              <input
                type="number"
                value={galleryForm.sort_order}
                onChange={(e) => setGalleryForm({ ...galleryForm, sort_order: e.target.value })}
                placeholder="Sort order"
                className="rounded-xl border border-[#d6c4b5] px-4 py-3 outline-none"
              />
              <select
                value={galleryForm.is_active}
                onChange={(e) => setGalleryForm({ ...galleryForm, is_active: e.target.value })}
                className="rounded-xl border border-[#d6c4b5] px-4 py-3 outline-none"
              >
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-[280px_1fr]">
              <div className="h-44 overflow-hidden rounded-xl border border-[#d8c6b7] bg-[#fbf3eb]">
                {galleryPreview ? (
                  <img src={galleryPreview} alt="Gallery preview" className="h-full w-full object-cover" />
                ) : (
                  <div className="grid h-full place-items-center text-sm text-[#9b877e]">No gallery photo selected</div>
                )}
              </div>
              <div className="flex flex-col justify-center gap-3">
                <label className="inline-flex w-fit cursor-pointer items-center gap-2 rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm font-semibold">
                  <Upload className="h-4 w-4" />
                  Upload Gallery Photo
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) => {
                      const file = event.target.files?.[0] || null;
                      setGalleryImageFile(file);
                      if (file) setGalleryPreview(URL.createObjectURL(file));
                    }}
                    className="hidden"
                  />
                </label>
                <div className="flex flex-wrap gap-3">
                  <button disabled={savingGallery} className="inline-flex items-center gap-2 rounded-xl bg-[#2B0000] px-5 py-3 text-sm font-semibold text-[#f4dfae] disabled:opacity-60">
                    <Save className="h-4 w-4" />
                    {savingGallery ? "Saving..." : galleryForm.id ? "Update Gallery Photo" : "Add Gallery Photo"}
                  </button>
                  {galleryForm.id && (
                    <button
                      type="button"
                      onClick={() => {
                        setGalleryForm(EMPTY_GALLERY);
                        setGalleryImageFile(null);
                        setGalleryPreview("");
                      }}
                      className="rounded-xl border border-[#d6c4b5] px-5 py-3 text-sm font-semibold"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          </form>
        </section>

        <section className="mt-5 overflow-hidden rounded-[24px] border border-[#d8c6b7] bg-[#fffaf5] shadow-sm">
          {galleryItems.map((item) => (
            <div key={item.id} className="flex flex-wrap items-center justify-between gap-4 border-b border-[#eadfd5] p-4 last:border-b-0">
              <div className="flex min-w-0 items-center gap-4">
                <span className="grid h-20 w-28 shrink-0 overflow-hidden rounded-xl bg-[#fbf3eb] text-xs text-[#9b877e]">
                  {item.image_url ? <img src={item.image_url} alt={item.title || "Gallery"} className="h-full w-full object-cover" /> : "No image"}
                </span>
                <div className="min-w-0">
                  <h3 className="truncate font-serif text-xl">{item.title || "Gallery photo"}</h3>
                  <p className="text-sm text-[#7d6c64]">Order {item.sort_order} - {item.is_active ? "Active" : "Inactive"}</p>
                </div>
              </div>
              <button onClick={() => editGalleryItem(item)} className="inline-flex items-center gap-2 rounded-xl border border-[#d6c4b5] bg-white px-4 py-2.5 text-sm font-semibold">
                <Edit3 className="h-4 w-4" />
                Edit
              </button>
            </div>
          ))}
          {!galleryItems.length && <p className="p-6 text-sm text-[#7d6c64]">No gallery photos added yet.</p>}
        </section>
      </div>
    </main>
  );
}
